/**
 * ComparePlans — Full tier comparison modal
 * Triggered from UpgradeTeaser "Compare all plans" link
 */
import React, { useState } from 'react';
import { X, Check, Minus, Zap, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

const TIERS = [
  {
    key: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    color: '#6b7280',
    icon: null,
    buttonLabel: 'Current Plan',
    trial: null,
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$29',
    period: '/mo',
    color: '#00d4ff',
    icon: Crown,
    trial: '7-day free trial, then $29/mo',
    popular: true,
    buttonLabel: 'Start 7-Day Free Trial',
  },
  {
    key: 'elite',
    name: 'Elite',
    price: '$79',
    period: '/mo',
    color: '#ffa502',
    icon: Zap,
    trial: null,
    buttonLabel: 'Go Elite',
  },
];

const FEATURES = [
  { label: 'Yield Explorer pools', free: 'limited', pro: '500+', elite: 'Unlimited' },
  { label: 'Risk Grade Badges (A/B/C/D/F)', free: true, pro: true, elite: true },
  { label: 'Security Score Badges', free: true, pro: true, elite: true },
  { label: 'Trending Chains Leaderboard', free: true, pro: true, elite: true },
  { label: 'Bitcoin DeFi Spotlight', free: true, pro: true, elite: true },
  { label: 'Full Risk Grade Details & Filter', free: false, pro: true, elite: true },
  { label: 'Full Security Detail Panel', free: false, pro: true, elite: true },
  { label: 'Gas Time-to-Profit (TTP)', free: false, pro: true, elite: true },
  { label: 'Full Simulator Labs', free: false, pro: true, elite: true },
  { label: 'Time-to-Profit Charts', free: false, pro: true, elite: true },
  { label: 'Portfolio Analyzer + Risk Grades', free: false, pro: true, elite: true },
  { label: '3 Pool APY/TVL Alerts', free: false, pro: true, elite: true },
  { label: 'Rate My Portfolio Optimization', free: false, pro: false, elite: true },
  { label: 'Whale Tracker (smart money)', free: false, pro: false, elite: true },
  { label: 'AI Pool Insights', free: false, pro: false, elite: true },
  { label: 'Forecast Dial (Monte Carlo + TTP)', free: false, pro: false, elite: true },
  { label: 'Advanced Backtesting', free: false, pro: false, elite: true },
  { label: 'Unlimited Alerts + Telegram', free: false, pro: false, elite: true },
  { label: 'CSV Data Export', free: false, pro: false, elite: true },
  { label: 'Priority Support', free: false, pro: false, elite: true },
];

export default function ComparePlans({ open, onClose }) {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(null);

  if (!open) return null;

  const handleUpgrade = async (tierKey) => {
    if (tierKey === 'free') return;
    if (!isAuthenticated) {
      window.dispatchEvent(new CustomEvent('profitspot:open-auth'));
      onClose();
      return;
    }
    setLoading(tierKey);
    try {
      const token = localStorage.getItem('profitspot_token');
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: tierKey, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast("Couldn’t start checkout. Please try again.");
    } catch {
      toast("Couldn’t start checkout. Please try again.");
    }
    setLoading(null);
  };

  const getCellValue = (feature, tierKey) => feature[tierKey];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="compare-plans-modal"
    >
      <div
        className="bg-[#0d0d1a] border border-gray-700/60 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Compare Plans</h2>
            <p className="text-xs text-gray-500 mt-0.5">Pick the plan that fits your DeFi strategy</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-gray-800 flex-shrink-0"
            data-testid="compare-plans-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 min-h-0 px-6 py-5">

          {/* Tier header cards */}
          <div className="grid grid-cols-5 gap-2 mb-5">
            {/* Empty feature label column */}
            <div />
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.key}
                  className={`rounded-xl p-3 text-center border transition-all ${
                    tier.popular
                      ? 'border-[#00d4ff]/40 bg-[#00d4ff]/08'
                      : 'border-gray-800 bg-gray-900/40'
                  }`}
                  data-testid={`compare-tier-${tier.key}`}
                >
                  {tier.popular && (
                    <div className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: tier.color }}>
                      Most Popular
                    </div>
                  )}
                  {Icon && (
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center mx-auto mb-1.5"
                      style={{ backgroundColor: `${tier.color}18`, border: `1px solid ${tier.color}30` }}
                    >
                      <Icon className="w-3.5 h-3.5" style={{ color: tier.color }} />
                    </div>
                  )}
                  <div className="font-bold text-white text-xs">{tier.name}</div>
                  <div className="mt-1 flex items-baseline justify-center gap-0.5">
                    <span className="text-lg font-bold leading-none" style={{ color: tier.color }}>{tier.price}</span>
                    <span className="text-[10px] text-gray-500">{tier.period}</span>
                  </div>
                  {tier.trial && (
                    <div className="text-[9px] text-gray-500 mt-1 leading-tight">{tier.trial}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feature rows */}
          <div className="rounded-xl overflow-hidden border border-gray-800/60">
            {FEATURES.map((feature, i) => (
              <div
                key={feature.label}
                className={`grid grid-cols-5 gap-2 items-center px-3 py-2.5 ${
                  i % 2 === 0 ? 'bg-gray-900/30' : 'bg-transparent'
                } ${i < FEATURES.length - 1 ? 'border-b border-gray-800/40' : ''}`}
              >
                <span className="text-xs text-gray-300 pr-2">{feature.label}</span>
                {TIERS.map((tier) => {
                  const val = getCellValue(feature, tier.key);
                  return (
                    <div key={tier.key} className="flex justify-center">
                      {val === true ? (
                        <Check className="w-3.5 h-3.5 text-[#00ff88]" />
                      ) : val === 'limited' ? (
                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">
                          Limited
                        </span>
                      ) : typeof val === 'string' ? (
                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#00ff88]/10 text-[#00ff88]">
                          {val}
                        </span>
                      ) : (
                        <Minus className="w-3.5 h-3.5 text-gray-700" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* CTA buttons row */}
          <div className="grid grid-cols-5 gap-2 mt-4">
            <div />
            {TIERS.map((tier) => (
              <div key={tier.key}>
                {tier.key === 'free' ? (
                  <div className="w-full py-2 rounded-xl text-[10px] font-medium text-gray-600 text-center border border-gray-800">
                    Free plan
                  </div>
                ) : (
                  <button
                    onClick={() => handleUpgrade(tier.key)}
                    disabled={loading === tier.key}
                    className="w-full py-2 rounded-xl text-[10px] font-bold text-black transition-all hover:brightness-110 disabled:opacity-50"
                    style={{ backgroundColor: tier.color }}
                    data-testid={`compare-upgrade-${tier.key}`}
                  >
                    {loading === tier.key
                      ? '...'
                      : isAuthenticated
                        ? tier.buttonLabel
                        : (tier.key === 'free' ? 'Sign in to start free' : `Get ${tier.name}`)}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Fine print */}
          <p className="text-[10px] text-gray-600 text-center mt-4">
            Free plan shows risk grades & security scores on all pools. Pro ($29/mo) unlocks full analytics, 7-day free trial. Elite ($79/mo) adds AI, whale tracking & forecasting.
          </p>
        </div>
      </div>
    </div>
  );
}
